import { action, API, controller } from "api";
import { useAppContext, useRegister } from "context";
import { FormErrorSummary, InputAuto } from "controls";
import { lsGet, lsSave, Model, UseForm, useValidationSummary } from "hooks";
import { Alert, Form } from "layout";
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import Header from "./Header";
import { REQUIRED, SWITCH } from "./modes";
import { publish, TOPIC } from "utils/tabSync";
import { useTranslation } from "react-i18next";

// mode: REQUIRED on first run, SWITCH when re-opened from the branch chip, null once
// the dialog has finished animating closed. It is the ONLY thing that decides what
// renders, and it is set and cleared by BizContext around the transition — never by a
// click — so nothing here changes shape while the dialog is still on screen.
const Body = ({ mode, onRequire, onClose }) => {

  const { t } = useTranslation(['common', 'forms', 'alerts']);
  const { setBizContext, setSelCompany } = useAppContext();
  // The till follows the branch, so a switch re-points POS at another drawer. What it
  // would throw away — a scanned basket, an invoice mid-edit — is published by POS.
  const { getCartGuard } = useRegister();
  const [alert, setAlert] = useState({ isOpen: false });
  // Gates the two pickers only. The form itself always renders: this dialog used to
  // swap its entire contents for a "Loading…" line, so re-opening it from the chip
  // flashed a bare box with no title and no buttons before jumping to the real thing.
  const [loading, setLoading] = useState(true);
  const [companies, setCompanies] = useState([]);
  const [outlets, setOutlets] = useState([]);
  const { BizContextMdl } = Model();
  const { values, setValues, errors, setErrors, handleInpChg } = UseForm(BizContextMdl);
  const { onInvalid, summaryProps } = useValidationSummary({ errors });

  // The labels ride along with the ids so every screen can say "you are working in
  // Salmiya" without another round trip — and so switching branch replaces the old
  // label immediately rather than leaving a stale one on screen.
  // outletOptions is passed explicitly by autoSelect: it runs in the same tick as
  // setOutlets, where the `outlets` state still holds the previous list.
  const withLabels = (bizValues, outletOptions = outlets) => ({
    ...bizValues,
    companyLabel: companies.find((c) => c.id === bizValues.companyId)?.label || "",
    outletLabel: outletOptions.find((o) => o.id === bizValues.outletId)?.label || "",
  });

  const commit = (bizValues, outletOptions) => {
    const next = withLabels(bizValues, outletOptions);
    setBizContext({ ...next });
    lsSave("bizContext", next);
    setSelCompany(next?.companyId);
    // Every other tab of this browser follows (App.jsx applies it, never re-sends).
    publish(TOPIC.BIZ_CONTEXT_CHANGED, next);
    onClose();
  };


  // ── First run, once ───────────────────────────────────────────────────────
  // layout/Dialog is keepMounted, so this component mounts at app start and never
  // unmounts. That makes this a genuinely one-shot effect: it answers "has a branch
  // been chosen yet?" and nothing more. Anything that has to happen when the dialog
  // is RE-opened belongs in the effect below, not here.
  useEffect(() => {
    const _bizContext = lsGet("bizContext");

    if (_bizContext === null || !_bizContext?.outletId) {
      onRequire();
      API(controller.Company + action.GetLabels).get((res) => {
        if (res?.status === 200) {
          const result = res?.result;
          setCompanies([...result]);
          // Auto-select if only one company
          if (result.length === 1) {
            setValues((j) => ({ ...j, companyId: result[0].id }));
          }
        } else toast.error(res?.msg);
        setLoading(false);
      });
    } else {
      setBizContext({ ..._bizContext });
      setSelCompany(_bizContext?.companyId);
      // Clearing this is not optional. `loading` starts true, and leaving it true on
      // the cached path left both pickers permanently disabled behind a hidden dialog
      // — which is exactly what the branch chip then revealed.
      setLoading(false);
    }

    return () => {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Re-opened on purpose ──────────────────────────────────────────────────
  // Keyed on the mode rather than on mount, for the keepMounted reason above: by the
  // time the user reaches for the branch chip this component mounted long ago and a
  // []-effect will never run again.
  useEffect(() => {
    if (mode !== SWITCH) return;

    // Pre-fill with where they are now, so the dialog opens already showing the current
    // branch instead of an empty form. The refresh below only replaces the OPTIONS, so
    // both fields stay readable while it is in flight.
    const cached = lsGet("bizContext") || {};
    setValues((j) => ({ ...j, ...cached }));

    // Seed the pickers from the labels cached alongside the ids. InputAuto resolves its
    // display text by finding the value in `options`, so a value with no matching option
    // renders BLANK — and on the first chip click of a session the lists have never been
    // fetched. Only ever fills an empty list; a real fetched list is left alone.
    if (cached.companyId && cached.companyLabel)
      setCompanies((prev) =>
        prev.length ? prev : [{ id: cached.companyId, label: cached.companyLabel }]);
    if (cached.outletId && cached.outletLabel)
      setOutlets((prev) =>
        prev.length ? prev : [{ id: cached.outletId, label: cached.outletLabel }]);

    setLoading(true);
    API(controller.Company + action.GetLabels).get((res) => {
      if (res?.status === 200) setCompanies([...res.result]);
      else toast.error(res?.msg);
      setLoading(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);


  useEffect(() => {
    if (!values?.companyId) return;
    API(controller.Outlet + action.GetLabelsByCo).getById(
      values.companyId,
      (res) => {
        if (res?.status === 200) {
          const result = res?.result;
          setOutlets([...result]);

          // Skipping the prompt when there is only one branch to pick is a FIRST-RUN
          // convenience, so it is gated on REQUIRED positively. It used to be gated on
          // `!isSwitching`, which is false both while switching AND once the dialog has
          // closed — so a single-branch company whose outlets landed during the close
          // had its Cancel silently turn into a save. `mode` is captured at request
          // time, which is the mode that asked for these outlets.
          if (result.length === 1 && mode === REQUIRED) {
            commit({ ...values, outletId: result[0].id }, result);
          } else if (result.length === 1) {
            setValues((j) => ({ ...j, outletId: result[0].id }));
          }
        } else toast.error(res?.msg);

      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values?.companyId]);

  const validateForm = () => {
    let temp = {};
    temp.companyId = values.companyId !== "" ? "" : t('alerts:companyRequired');
    temp.outletId = values.outletId !== "" ? "" : t('alerts:outletRequired');

    setErrors({ ...temp });
    return Object.values(temp).every((x) => x === "");
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) return onInvalid();

    // Prices, stock and tax in a basket were looked up in the branch being left, so it
    // cannot follow the cashier. Ask before clearing it rather than losing it silently.
    const branchChanges =
      Number(values.outletId) !== Number(lsGet("bizContext")?.outletId || 0);
    const guard = getCartGuard();
    if (branchChanges && guard && (guard.count > 0 || guard.editing)) {
      setAlert({
        isOpen: true,
        type: "confirmation",
        title: t("alerts:branchSwitchCartTitle"),
        subTitle: guard.editing
          ? t("alerts:branchSwitchEditBody")
          : t("alerts:branchSwitchCartBody", { count: guard.count }),
        confirmBtnTitle: t("alerts:branchSwitchClear"),
        cancelBtnTitle: t("alerts:branchSwitchStay"),
        confirmBtnColor: "warning",
        onCancel: () => setAlert({ isOpen: false }),
        onConfirm: () => {
          guard.clear();
          setAlert({ isOpen: false });
          commit(values);
        },
      });
      return;
    }

    commit(values);
  };

  return (
    <>
    <Form onSubmit={handleSubmit} noValidate>
      {/* -------- Header -------- */}
      <Header {...{ mode, onCancel: onClose }} />
      {/* -------- Body -------- */}

      <div className="flex flex-col gap-5 p-[12px] mt-[12px]">
        <FormErrorSummary {...summaryProps} />
        <InputAuto
          fullWidth
          disabled={loading}
          label={t('forms:company')}
          name="companyId"
          value={values.companyId}
          error={errors.companyId}
          required={true}
          onChange={handleInpChg}
          options={companies}
        />

        <InputAuto
          disabled={loading || !values?.companyId}
          fullWidth
          label={t('outlet')}
          name="outletId"
          value={values.outletId}
          error={errors.outletId}
          required={true}
          onChange={handleInpChg}
          options={outlets}
        />
      </div>
    </Form>
    <Alert {...{ alert, setAlert }} />
    </>
  );
};

export default Body;
